import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { getAnalytics } from '../../utils/chatbotAnalytics';

const AdminContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  margin: theme.spacing(3),
  maxWidth: 1200,
  marginLeft: 'auto',
  marginRight: 'auto',
}));

const TabPanel = (props) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`chatbot-tabpanel-${index}`}
      aria-labelledby={`chatbot-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

const ChatbotAdmin = () => {
  const [tabValue, setTabValue] = useState(0);
  const [analytics, setAnalytics] = useState(null);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    // Load analytics data
    const data = getAnalytics();
    setAnalytics(data);
  }, []);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleViewConversation = (conversation) => {
    setSelectedConversation(conversation);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  if (!analytics) {
    return (
      <AdminContainer>
        <Typography variant="h4" gutterBottom>
          Chatbot Analytics
        </Typography>
        <Typography>Loading analytics data...</Typography>
      </AdminContainer>
    );
  }

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  return (
    <AdminContainer>
      <Typography variant="h4" gutterBottom>
        Chatbot Analytics
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="chatbot analytics tabs">
          <Tab label="Overview" />
          <Tab label="Conversations" />
          <Tab label="Feedback" />
          <Tab label="Improvement Suggestions" />
        </Tabs>
      </Box>

      {/* Overview Tab */}
      <TabPanel value={tabValue} index={0}>
        <Box display="flex" flexWrap="wrap" gap={3}>
          <Paper sx={{ p: 2, flex: '1 1 200px' }}>
            <Typography variant="h6" gutterBottom>
              Total Sessions
            </Typography>
            <Typography variant="h3">{analytics.sessionCount}</Typography>
          </Paper>

          <Paper sx={{ p: 2, flex: '1 1 200px' }}>
            <Typography variant="h6" gutterBottom>
              Total Interactions
            </Typography>
            <Typography variant="h3">{analytics.totalInteractions}</Typography>
          </Paper>

          <Paper sx={{ p: 2, flex: '1 1 200px' }}>
            <Typography variant="h6" gutterBottom>
              Positive Feedback
            </Typography>
            <Typography variant="h3">
              {analytics.feedback.filter((f) => f.isPositive).length}
            </Typography>
          </Paper>

          <Paper sx={{ p: 2, flex: '1 1 200px' }}>
            <Typography variant="h6" gutterBottom>
              Improvement Suggestions
            </Typography>
            <Typography variant="h3">{analytics.improvementSuggestions.length}</Typography>
          </Paper>
        </Box>

        <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
          Top Queries
        </Typography>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Query</TableCell>
                <TableCell align="right">Count</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(analytics.topQueries)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 10)
                .map(([query, count]) => (
                  <TableRow key={query}>
                    <TableCell>{query}</TableCell>
                    <TableCell align="right">{count}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Conversations Tab */}
      <TabPanel value={tabValue} index={1}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Session ID</TableCell>
                <TableCell>Start Time</TableCell>
                <TableCell>End Time</TableCell>
                <TableCell>Messages</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {analytics.conversations.map((conversation) => (
                <TableRow key={conversation.sessionId}>
                  <TableCell>{conversation.sessionId}</TableCell>
                  <TableCell>{formatDate(conversation.startTime)}</TableCell>
                  <TableCell>
                    {conversation.endTime ? formatDate(conversation.endTime) : 'Active'}
                  </TableCell>
                  <TableCell>{conversation.messages.length}</TableCell>
                  <TableCell>
                    <Chip
                      label={conversation.resolved ? 'Resolved' : 'Active'}
                      color={conversation.resolved ? 'success' : 'primary'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleViewConversation(conversation)}
                    >
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Feedback Tab */}
      <TabPanel value={tabValue} index={2}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Message ID</TableCell>
                <TableCell>Feedback</TableCell>
                <TableCell>Timestamp</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {analytics.feedback.map((feedback, index) => (
                <TableRow key={index}>
                  <TableCell>{feedback.messageId}</TableCell>
                  <TableCell>
                    <Chip
                      label={feedback.isPositive ? 'Positive' : 'Negative'}
                      color={feedback.isPositive ? 'success' : 'error'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatDate(feedback.timestamp)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Improvement Suggestions Tab */}
      <TabPanel value={tabValue} index={3}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Message ID</TableCell>
                <TableCell>Suggestion</TableCell>
                <TableCell>Timestamp</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {analytics.improvementSuggestions.map((suggestion, index) => (
                <TableRow key={index}>
                  <TableCell>{suggestion.messageId}</TableCell>
                  <TableCell>{suggestion.suggestion}</TableCell>
                  <TableCell>{formatDate(suggestion.timestamp)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Conversation Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>Conversation Details</DialogTitle>
        <DialogContent dividers>
          {selectedConversation && (
            <>
              <Typography variant="subtitle2" gutterBottom>
                Session ID: {selectedConversation.sessionId}
              </Typography>
              <Typography variant="subtitle2" gutterBottom>
                Start Time: {formatDate(selectedConversation.startTime)}
              </Typography>
              {selectedConversation.endTime && (
                <Typography variant="subtitle2" gutterBottom>
                  End Time: {formatDate(selectedConversation.endTime)}
                </Typography>
              )}
              <Typography variant="subtitle2" gutterBottom>
                Status:{' '}
                <Chip
                  label={selectedConversation.resolved ? 'Resolved' : 'Active'}
                  color={selectedConversation.resolved ? 'success' : 'primary'}
                  size="small"
                />
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" gutterBottom>
                Messages
              </Typography>

              {selectedConversation.messages.map((message, index) => (
                <Paper
                  key={index}
                  sx={{
                    p: 2,
                    mb: 2,
                    backgroundColor: message.type === 'user' ? '#f5f5f5' : '#e3f2fd',
                  }}
                >
                  <Typography variant="subtitle2">
                    {message.type === 'user' ? 'User' : 'Bot'} - {formatDate(message.timestamp)}
                  </Typography>
                  <Typography variant="body1">{message.text}</Typography>
                </Paper>
              ))}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </AdminContainer>
  );
};

export default ChatbotAdmin;
